import { useEffect, useMemo, useState } from "react";
import {
  getRedirects,
  saveRedirects,
  type RedirectDoc,
  type RedirectHit,
} from "../lib/contentApi";
import { ActionButton } from "./ActionButton";
import { useRegisterSave } from "../lib/saveBar";
import { Msg } from "./ui";
import { qrRemovedOnSave, qrRemovedSay } from "../lib/qrRemoved";

/**
 * QR/NFC 코드 편집(FR-QR).
 *
 * 왜 필요한가: QR 은 책·굿즈·현수막에 **인쇄**된다. 목적지 페이지가 바뀌거나 없어져도
 * 종이는 회수할 수 없다. 배포를 기다리는 동안 스캔한 아이는 막다른 길을 본다.
 *
 * ⚠️ 목적지는 **우리 사이트 안의 주소(`/`로 시작)만** 넣을 수 있다. 외부 주소를 넣으면
 * 우리 QR 이 아이를 임의의 사이트로 보내는 통로가 되므로 서버가 거부한다.
 */
const WEB_BASE = (import.meta.env.VITE_WEB_BASE as string | undefined) ?? "https://woorinabiglobal.com";

/** 자주 쓰는 목적지 — 운영자가 경로를 외우지 않아도 되게. */
const TARGETS = [
  { path: "/", label: "홈(랜딩)" },
  { path: "/book", label: "전자책방" },
  { path: "/world", label: "체험 공간" },
  { path: "/world?zone=gyeongju", label: "체험 공간 · 경주" },
  { path: "/world?zone=event", label: "체험 공간 · 행사장" },
  { path: "/goods", label: "굿즈 목록" },
  { path: "/rally", label: "경주 스탬프 랠리" },
  { path: "/ar", label: "AR 체험" },
  { path: "/community", label: "마을 소식" },
];

export function RedirectPanel() {
  const [base, setBase] = useState<RedirectDoc[]>([]);
  const [rows, setRows] = useState<RedirectDoc[]>([]);
  /** 불러온 그대로의 목록 — 저장할 때 「무엇이 빠지나」를 이것과 견준다(qrRemoved.ts) */
  const [loadedRows, setLoadedRows] = useState<RedirectDoc[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  /** 코드별 들어온 횟수 — 인쇄 전 시험 스캔이 「살아 있다」를 말해 준다. */
  const [hits, setHits] = useState<RedirectHit[]>([]);
  /**
   * 🔴 **등록 안 된 코드로 들어온 것.** 인쇄물의 코드가 우리 표에 없다는 뜻이고,
   * 스캔한 아이는 홈으로 갔다. 종이는 회수할 수 없으니 **여기에 코드를 추가**해야 고쳐진다.
   */
  const [unknown, setUnknown] = useState<RedirectHit[]>([]);

  /**
   * 🔴 **몇 번 들어왔나를 기간으로 끊어 본다.** 비어 있으면 처음부터 지금까지의 누계다.
   *
   * 2026-09-21 — 보고서에 「8. 1.~9. 20. QR 진입 수」를 써야 하는데 이 화면은 누계뿐이라
   * 그 값을 못 뽑았다(방문자수 화면은 이미 날짜 지정이 됐다).
   */
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  /** 지금 화면에 보이는 숫자가 **어느 기간인지**. 서버가 알려 준 대로만 말한다. */
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  async function refresh(period?: { from: string; to: string }) {
    const r = await getRedirects(period);
    setBase(r.base);
    setHits(r.hits);
    setUnknown(r.unknown);
    setRange(r.range);
    // 편집 화면은 **병합 결과**를 보여준다 — 운영자에게는 "지금 이 QR 이 어디로 가는가"가 전부다.
    setRows(r.merged);
    setLoadedRows(r.merged);
    setLoaded(true);
  }
  useEffect(() => {
    refresh().catch(() => setMsg("엣지 서버에 연결하지 못했어요."));
  }, []);

  const totalHits = useMemo(() => hits.reduce((s, h) => s + h.n, 0), [hits]);

  const baseByCode = useMemo(() => new Map(base.map((e) => [e.code.toLowerCase(), e.target])), [base]);
  const hitByCode = useMemo(
    () => new Map(hits.map((h) => [h.code.toLowerCase(), h.n])),
    [hits],
  );

  /** 모르는 코드를 표에 넣어 준다 — 목적지는 홈으로 두고 운영자가 고른다. */
  function adopt(code: string) {
    setRows((prev) =>
      prev.some((r) => r.code.trim().toLowerCase() === code.toLowerCase())
        ? prev
        : [...prev, { code, target: "/", utm: { source: "qr", medium: "print" } }],
    );
    setMsg(`'${code}' 를 아래 표에 넣었어요. 목적지를 고르고 저장해 주세요.`);
  }

  function update(i: number, patch: Partial<RedirectDoc>) {
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }
  function add() {
    setRows((prev) => [...prev, { code: "", target: "/", utm: { source: "qr", medium: "print" } }]);
  }
  function remove(i: number) {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  }

  async function onSave() {
    const clean = rows.filter((r) => r.code.trim() && r.target.trim());
    // 🧾 빠지는 코드가 있으면 묻는다 — 인쇄된 QR 이 홈으로 가게 되거나, 지운 줄이 안 지워진다(qrRemoved.ts)
    const gone = qrRemovedOnSave(loadedRows, clean, base, hits);
    if (gone.length && !window.confirm(qrRemovedSay(gone, range ? `${range.from || "처음"}~${range.to || "오늘"}에` : "지금까지"))) {
      setMsg(`⏸️ 안 저장했어요 — 빠지는 코드: ${gone.map((g) => g.code).join(", ")}. 지운 게 아니면 [+ 코드 추가]로 다시 넣어 주세요.`);
      throw new Error("빠지는 QR 코드 확인에서 취소 — 안 저장했어요");
    }
    const r = await saveRedirects(clean);
    await refresh();
    setMsg(
      `✅ ${r.count}개를 저장했어요.` +
        (r.dropped
          ? ` ${r.dropped}개는 우리 사이트 밖 주소라 넣지 않았어요(아이를 외부로 보내지 않기 위해서예요).`
          : "") +
        " 1분 안에 모든 QR 에 적용돼요.",
    );
  }
  useRegisterSave({
    id: "qr",
    label: "QR 코드",
    save: onSave,
    draft: { data: () => rows, restore: (d) => setRows(d as RedirectDoc[]) },
  });

  async function onReset() {
    await saveRedirects([]);
    await refresh();
    setMsg("↩️ 처음 설정(파일에 적힌 목적지)으로 되돌렸어요.");
  }

  return (
    <section className="panel qrpanel">
      <p className="hint">
        종이는 다시 찍을 수 없어요. <b>페이지가 바뀌면 여기서 목적지만 고치면 돼요.</b>
        우리 사이트 안 주소(/로 시작)만 넣을 수 있어요.
      </p>

      <div className="qrpanel__actions">
        <button className="btn" onClick={add} disabled={!loaded}>+ 코드 추가</button>
        <ActionButton
          onAction={onReset}
          doneLabel="되돌렸어요"
          className="btn btn--sm"
          confirm="고친 목적지를 버리고 처음 설정으로 되돌릴까요?"
        >
          처음 설정으로
        </ActionButton>
      </div>

      <div className="qrpanel__period">
        <label>
          언제부터{" "}
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to || undefined} />
        </label>
        <label>
          언제까지{" "}
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from || undefined} />
        </label>
        <button
          type="button"
          className="btn btn--sm"
          disabled={!from || !to}
          onClick={() =>
            refresh({ from, to }).catch(() => setMsg("기간별 숫자를 못 받았어요. 잠시 뒤 다시 눌러 주세요."))
          }
        >
          그 기간만 세기
        </button>
        {range ? (
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => {
              setFrom("");
              setTo("");
              refresh().catch(() => setMsg("전체 기간을 못 받았어요."));
            }}
          >
            전체 기간으로
          </button>
        ) : null}
        {/* 🔴 지금 보이는 숫자가 무슨 기간인지 **화면이 말해야 한다** — 안 그러면 누계를 기간 값으로 적는다 */}
        <span className="qrpanel__periodsay" role="status">
          {range ? `${range.from} ~ ${range.to} · 합계 ${totalHits}번` : `전체 기간 합계 ${totalHits}번`}
        </span>
      </div>

      {unknown.length > 0 ? (
        <div className="qrpanel__unknown" role="status">
          <p>
            ⚠️ <b>표에 없는 코드로 {unknown.reduce((s, h) => s + h.n, 0)}번</b> 들어왔어요
            {range ? ` (${range.from} ~ ${range.to})` : ""}.
            인쇄물의 코드가 우리 표에 없다는 뜻이고, 그 QR 을 찍은 사람은 홈으로 갔어요.
            <b> 종이는 다시 찍을 수 없으니 여기에 그 코드를 넣어 주세요.</b>
          </p>
          <ul>
            {unknown.map((h) => (
              <li key={h.code}>
                <code>{h.code}</code> — {h.n}번
                <button type="button" className="btn btn--sm" onClick={() => adopt(h.code)}>
                  이 코드 넣기
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Msg text={msg} />

      <ul className="qrpanel__list">
        {rows.map((r, i) => {
          const orig = baseByCode.get(r.code.trim().toLowerCase());
          const changed = orig !== undefined && orig !== r.target;
          const scanUrl = `${WEB_BASE}/go/${r.code || "코드"}`;
          // 🔴 인쇄 **전에** 시험 스캔이 「이 QR 이 살아 있다」를 말해 준다.
          //    0 이면 아직 아무도 안 찍은 것 — 인쇄 전이면 그게 정상이다
          const n = hitByCode.get(r.code.trim().toLowerCase()) ?? 0;
          return (
            <li key={i} className="qrpanel__item" data-changed={changed || undefined}>
              <div className="qrpanel__row">
                <label>
                  코드
                  <input
                    value={r.code}
                    placeholder="b03"
                    onChange={(e) => update(i, { code: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })}
                  />
                </label>
                <label className="qrpanel__grow">
                  어디로 보낼까요 (우리 사이트 주소만)
                  <input
                    value={r.target}
                    placeholder="/world"
                    onChange={(e) => update(i, { target: e.target.value })}
                  />
                </label>
                <label>
                  자주 쓰는 곳
                  <select value="" onChange={(e) => e.target.value && update(i, { target: e.target.value })}>
                    <option value="">고르기…</option>
                    {TARGETS.map((t) => (
                      <option key={t.path} value={t.path}>{t.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="qrpanel__foot">
                <code className="qrpanel__url">{scanUrl}</code>
                {changed ? <span className="qrpanel__badge">처음엔 {orig} 였어요</span> : null}
                <span className="qrpanel__hits">
                  {n > 0 ? `${n}번 들어왔어요` : "아직 들어온 적 없어요"}
                </span>
                <a className="btn btn--sm" href={scanUrl} target="_blank" rel="noopener noreferrer">
                  스캔해 보기 ↗
                </a>
                <button className="btn btn--sm" onClick={() => remove(i)}>삭제</button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
